import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  selectHasNewVersion,
  setHasNewVersion,
} from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { BUILD_NUMBER, VERSION } from '@backrs_ui/grow-ui/src/version/version';

const THROTTLE_MS = 60_000;

const VERSION_PLACEHOLDER = 'VERSION_PLACEHOLDER';
const BUILD_NUMBER_PLACEHOLDER = 'BUILD_NUMBER_PLACEHOLDER';

type DeployedVersionPayload = Readonly<{
  version: string;
  buildNumber: string;
}>;

const parseParts = (version: string, buildNumber: string): number[] => {
  const versionParts = version.split('.').map((segment) => Number(segment));
  const buildPart = Number(buildNumber);

  if (versionParts.some(Number.isNaN) || Number.isNaN(buildPart)) {
    return [];
  }

  return [...versionParts, buildPart];
};

const isDeployedNewer = (deployed: readonly number[], bundle: readonly number[]): boolean => {
  if (deployed.length === 0 || bundle.length === 0) return false;

  const length = Math.max(deployed.length, bundle.length);

  for (let i = 0; i < length; i++) {
    const d = deployed[i] ?? 0;
    const b = bundle[i] ?? 0;

    if (d > b) return true;
    if (d < b) return false;
  }

  return false;
};

export const useNewVersionCheck = (): void => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const hasNewVersion = useAppSelector(selectHasNewVersion);

  const lastCheckedAtRef = useRef<number>(0);
  const hasWarnedOnFetchFailureRef = useRef<boolean>(false);
  const hasWarnedOnPayloadShapeRef = useRef<boolean>(false);
  const hasWarnedOnParseFailureRef = useRef<boolean>(false);
  const hasNewVersionRef = useRef<boolean>(hasNewVersion);

  useEffect(() => {
    hasNewVersionRef.current = hasNewVersion;
  }, [hasNewVersion]);

  const runCheck = useCallback(async () => {
    if (VERSION === VERSION_PLACEHOLDER || BUILD_NUMBER === BUILD_NUMBER_PLACEHOLDER) {
      if (process.env.NODE_ENV === 'production') {
        console.warn(
          '[grow-ui] version metadata not set — VERSION_PLACEHOLDER/BUILD_NUMBER_PLACEHOLDER ' +
            'still present in the bundle. writeVersion.ts may not have run during the build.',
        );
      }

      return;
    }

    const now = Date.now();

    if (now - lastCheckedAtRef.current < THROTTLE_MS) return;

    lastCheckedAtRef.current = now;

    let deployed: DeployedVersionPayload;

    try {
      const response = await fetch(`/version.json?t=${now}`, { cache: 'no-store' });

      if (!response.ok) {
        if (!hasWarnedOnFetchFailureRef.current) {
          console.warn('[grow-ui] version.json fetch failed', { status: response.status });
          hasWarnedOnFetchFailureRef.current = true;
        }

        return;
      }

      deployed = (await response.json()) as DeployedVersionPayload;
    } catch (error) {
      if (!hasWarnedOnFetchFailureRef.current) {
        console.warn('[grow-ui] version.json fetch errored', error);
        hasWarnedOnFetchFailureRef.current = true;
      }

      return;
    }

    if (typeof deployed?.version !== 'string' || typeof deployed?.buildNumber !== 'string') {
      if (!hasWarnedOnPayloadShapeRef.current) {
        console.warn('[grow-ui] version.json payload missing fields', { deployed });
        hasWarnedOnPayloadShapeRef.current = true;
      }

      return;
    }

    const bundleParts = parseParts(VERSION, BUILD_NUMBER);
    const deployedParts = parseParts(deployed.version, deployed.buildNumber);

    if (deployedParts.length === 0 || bundleParts.length === 0) {
      if (!hasWarnedOnParseFailureRef.current) {
        console.warn('[grow-ui] could not parse version parts', {
          deployed,
          bundle: { version: VERSION, buildNumber: BUILD_NUMBER },
        });
        hasWarnedOnParseFailureRef.current = true;
      }

      return;
    }

    console.log('[version-check]', {
      bundle: `${VERSION}.${BUILD_NUMBER}`,
      deployed: `${deployed.version}.${deployed.buildNumber}`,
    });

    const shouldShow = isDeployedNewer(deployedParts, bundleParts);

    if (shouldShow !== hasNewVersionRef.current) {
      dispatch(setHasNewVersion(shouldShow));
    }
  }, [dispatch]);

  useEffect(() => {
    runCheck();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        runCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // runCheck is stable (only depends on dispatch). Empty deps would lint-fail.
  }, [runCheck]);

  useEffect(() => {
    runCheck();
  }, [location.pathname, runCheck]);
};
